import type { CreditStatus, DelayCategory, PaymentTermGapKind } from './enums';

export interface PaymentTermGapTransactionDto {
  // Acomba reference — NoAutoTransaction (invoice) or NoAutoInscriptionClient (inscription) —
  // used to drill into the right AcombaGO window.
  transactionReference: string;
  // User-facing number shown in the missing-information banner: the NoFacture for an invoice, the
  // inscription number for an inscription. Empty when the source supplies none.
  documentNumber: string;
  // Whether the gap is on an invoice or a customer inscription (selects the drilldown).
  kind: PaymentTermGapKind;
}

export interface RelanceFlagsDto {
  hasEmail: boolean;
  hasPaymentTerm: boolean;
  hasLanguage: boolean;
  hasContactName: boolean;
  creditStatus: CreditStatus;
  // Unpaid invoices missing their payment condition. Empty when hasPaymentTerm is true.
  paymentTermGapTransactions: PaymentTermGapTransactionDto[];
}

export interface RelanceListItemDto {
  id: string;
  customerId: string;
  customerExternalId: number | null;
  customerName: string;
  delayCategory: DelayCategory | null;
  flags: RelanceFlagsDto;
  daysLate: number | null;
  daysSinceLastRelance: number | null;
  creditLimitReachedPercent: number | null;
  relanceCount: number;
  totalDue: number;
  overdueCount: number | null;
  overdueDocumentCount: number | null;
  isActive: boolean;
  snoozeUntil: string | null;
  deactivatedAt: string | null;
  deactivationNote: string | null;
  transactionReference: string | null;
  paidAt?: string | null;
  paidAmountReceived?: number | null;
  paidRemainingBalance?: number | null;
  paidNote?: string | null;
  isFullPayment?: boolean | null;
  statementSentOn?: string | null;
  sentAt?: string | null;
}

export interface RelanceDto {
  id: string;
  customerId: string;
  customerExternalId: number | null;
  customerName: string;
  contactName: string;
  contactEmail: string;
  assignedAgent: string | null;
  historyUrl: string | null;
  receivablesTotal: number | null;
  totalDue: number | null;
  contactPhone: string | null;
  language: string | null;
  contactAddress: string | null;
  contactCity: string | null;
  contactPostalCode: string | null;
  contactCountryReference: string | null;
  /**
   * How many ACCEO Transphere payment links the reminder can carry — a count, never the links. The
   * server composes the block from the URLs it resolved, so the browser has no use for them. Null
   * means "not loaded" (the list endpoint) rather than "none".
   */
  paymentLinkCount: number | null;
  /** The last note written when one of this customer's relances was snoozed or ignored. */
  latestNote?: string | null;
  /** How many relances this customer was sent before this one. Null on the list endpoint. */
  relanceCount?: number | null;
  /** Null unless a run is in flight for this relance. Only the detail read reports it. */
  stage: RelanceStage | null;
  statementSentOn?: string | null;
  /** When this relance's email was sent (UTC ISO); null while it has not been sent. */
  sentAt: string | null;
}

/**
 * How far a relance's AI run has got, when one is in flight. Absent (null) means no run is in flight:
 * either the text is already stored, or nothing is being generated for this relance right now.
 */
export enum RelanceStage {
  /** Dispatched, waiting for a worker: the queues are shared across tenants, so this is a wait on capacity. */
  Queued = 'Queued',
  /** A worker is producing the recommendation; nothing is stored yet. */
  Generating = 'Generating',
  /** The recap is stored; the email draft cascade is dispatched but not yet claimed. */
  Recommendation = 'Recommendation',
  /** The run ended without its text — timed out, or failed past its retries. */
  Failed = 'Failed',
  /** The email draft is being produced from the stored recommendation. */
  Drafting = 'Drafting',
}

export interface RelanceNoteDto {
  contact: string;
  phone: string;
  content: string;
  nextRelance: string;
}

export interface RelanceRecapDto {
  relanceId: string;
  title: string | null;
  content: string;
  generatedAt: string;
}

/** A relance the last sync expects to open on `expectedOn`. */
export interface ExpectedRelanceDto {
  customerId: string;
  customerExternalId: number | null;
  customerName: string;
  /** `YYYY-MM-DD`, a UTC calendar day. */
  expectedOn: string;
  projectedTotalDue: number;
  projectedOverdueDocumentCount: number;
  relanceCount: number;
  /** Set when the relance is already open but not yet past due; null for one the next sync is projected to open. */
  relanceId?: string | null;
}
